"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"

// Fades + slides its children in the first time they scroll into view.
// Uses IntersectionObserver (not scroll-timeline CSS) for broad browser
// support. Anything already in view on mount reveals immediately, so the
// first photo in a gallery doesn't sit invisible.
export function RevealOnScroll({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Respect users who've asked for reduced motion — reveal instantly.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShown(true)
      return
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          io.disconnect()
        }
      },
      { threshold: 0.15 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
    >
      {children}
    </div>
  )
}
